/**
 * Approve Request Dialog — W3-A · B7 / B7.5.
 *
 * Confirms an approval on a workflow instance. Backend contract:
 *   POST /api/workflows/instances/{id}/approve
 *     Idempotency-Key: <client-generated>
 *     body: { note?: string, organisational_context_override? }
 *
 * B7.5 (2026-08-02) — Approver-Completes RBAC refinement.  When the
 * instance's payload is context-pending (i.e. the initiator lacked
 * `bu_master:read` and omitted BU + CC), the approver must bind the
 * chart-of-accounts placement inside this dialog before Approve fires.
 * The backend enforces the binding independently.
 *
 * Self-approval is blocked server-side (409); the caller is expected
 * to hide the Approve action for instances they initiated so the
 * server response is only a safety net.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";

import { api, formatApiError } from "../../../lib/api";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader,
    DialogTitle, DialogDescription,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";


function newIdemKey(instanceId) {
    return `ap-${instanceId}-${Date.now()}-${Math.random()
        .toString(36).slice(2, 8)}`;
}


export default function ApproveDialog({ open, instance, onClose, onApproved }) {
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [key, setKey] = useState("");
    // B7.5 — approver-side BU/CC binding for context-pending payloads.
    const [businessUnits, setBusinessUnits] = useState([]);
    const [costCentres, setCostCentres] = useState([]);
    const [ccLoading, setCcLoading] = useState(false);
    const [buId, setBuId] = useState("");
    const [ccId, setCcId] = useState("");
    const [initiative, setInitiative] = useState("");

    const contextPending = Boolean(
        instance?.payload?.context_pending
        || instance?.payload?.context_source === "pending",
    );

    useEffect(() => {
        if (open && instance) {
            setNote("");
            setSaving(false);
            setKey(newIdemKey(instance.workflow_instance_id));
            setBuId("");
            setCcId("");
            setInitiative("");
            setCostCentres([]);
            if (contextPending) {
                // Approver holds bu_master:read (super_admin only in B5).
                api.get("/org/business-unit/list?page_size=200")
                    .then((r) => setBusinessUnits(r.data?.items || []))
                    .catch(() => setBusinessUnits([]));
            } else {
                setBusinessUnits([]);
            }
        }
    }, [open, instance, contextPending]);

    useEffect(() => {
        if (!open || !contextPending || !buId) {
            setCostCentres([]);
            return;
        }
        setCcLoading(true);
        api.get(
            `/org/cost-centre/list?business_unit_id=${encodeURIComponent(buId)}&page_size=200`,
        )
            .then((r) => setCostCentres(r.data?.items || []))
            .catch(() => setCostCentres([]))
            .finally(() => setCcLoading(false));
    }, [open, contextPending, buId]);

    if (!instance) return null;

    const projectName = instance.payload?.name || "this Project Request";
    const bindingIncomplete = contextPending && (
        !buId || !ccId || !initiative.trim()
    );

    async function submit() {
        if (bindingIncomplete) return;
        setSaving(true);
        try {
            const body = { note: note.trim() || null };
            if (contextPending) {
                body.organisational_context_override = {
                    business_unit_id: buId,
                    cost_centre_id: ccId,
                    initiative: initiative.trim(),
                };
            }
            const r = await api.post(
                `/workflows/instances/${instance.workflow_instance_id}/approve`,
                body,
                { headers: { "Idempotency-Key": key } },
            );
            toast.success("Request approved. Active Project created.");
            onApproved?.(r.data);
        } catch (err) {
            toast.error(formatApiError(err) || "Approval failed.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-lg"
                data-testid="approve-request-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-emerald-800">
                        <CheckCircle
                            className="w-6 h-6 text-emerald-600"
                            weight="fill"
                        />
                        Approve Request
                    </DialogTitle>
                    <DialogDescription>
                        You are about to approve the Project Request
                        <strong> &ldquo;{projectName}&rdquo;</strong>. On approval,
                        an Active Project is created immediately and
                        the initiator is notified.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {contextPending ? (
                        <div
                            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1"
                            data-testid="approve-context-pending-note"
                        >
                            <div className="font-medium">
                                The initiator did not supply Business Unit
                                or Cost Centre.
                            </div>
                            <div>
                                Bind the correct chart-of-accounts placement
                                below — the Active Project is created against
                                the values you select.
                            </div>
                        </div>
                    ) : null}

                    {contextPending ? (
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <Label htmlFor="approve-bu">
                                    Business Unit <span className="text-rose-600">*</span>
                                </Label>
                                <Select
                                    value={buId}
                                    onValueChange={(v) => {
                                        setBuId(v);
                                        setCcId("");
                                    }}
                                >
                                    <SelectTrigger
                                        id="approve-bu"
                                        data-testid="approve-bu-trigger"
                                    >
                                        <SelectValue placeholder="Choose a Business Unit..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {businessUnits.map((bu) => (
                                            <SelectItem
                                                key={bu.id}
                                                value={bu.id}
                                                data-testid={`approve-bu-option-${bu.id}`}
                                            >
                                                {bu.name} ({bu.code})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="approve-cc">
                                    Cost Centre <span className="text-rose-600">*</span>
                                </Label>
                                <Select
                                    value={ccId}
                                    onValueChange={setCcId}
                                    disabled={!buId || ccLoading}
                                >
                                    <SelectTrigger
                                        id="approve-cc"
                                        data-testid="approve-cc-trigger"
                                    >
                                        <SelectValue placeholder={
                                            !buId
                                                ? "Select a Business Unit first"
                                                : ccLoading
                                                    ? "Loading..."
                                                    : (costCentres.length === 0
                                                        ? "No Cost Centres for this Business Unit"
                                                        : "Choose a Cost Centre...")
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {costCentres.map((cc) => (
                                            <SelectItem
                                                key={cc.id}
                                                value={cc.id}
                                                data-testid={`approve-cc-option-${cc.id}`}
                                            >
                                                {cc.name} ({cc.code})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="approve-initiative">
                                    Initiative <span className="text-rose-600">*</span>
                                </Label>
                                <Input
                                    id="approve-initiative"
                                    value={initiative}
                                    onChange={(e) => setInitiative(e.target.value)}
                                    placeholder="e.g. FY26 onboarding automation"
                                    data-testid="approve-initiative-input"
                                />
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <Label htmlFor="approve-note">
                            Approver note (optional)
                        </Label>
                        <Textarea
                            id="approve-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            placeholder="Any note the initiator should see (optional)…"
                            data-testid="approve-note-input"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="approve-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving || bindingIncomplete}
                        data-testid="approve-confirm-btn"
                    >
                        {saving ? "Approving…" : "Approve Request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
